import {getFirstName} from '@/utils/getFirstName';

import {CO_PARENT_INVITATION_STORAGE_KEY} from './constants';
import {CoParentInvitation} from './interfaces';

/**
 * Reads the co-parent invitation persisted by {@link CoparentViewModelProvider}.
 */
export const getCoParentInvitationFromStorage =
  (): CoParentInvitation | null => {
    try {
      const raw = localStorage.getItem(CO_PARENT_INVITATION_STORAGE_KEY);
      if (!raw) return null;
      return JSON.parse(raw) as CoParentInvitation;
    } catch {
      return null;
    }
  };

/**
 * Resolves the inviter name from persisted invitation data.
 */
export const getInvitationInviterName = (
  invitation: CoParentInvitation,
): string | undefined => {
  const {rawParams} = invitation;

  return invitation.inviterName || rawParams?.inviter_name || undefined;
};

/**
 * Resolves the name to pre-fill on sign-up from persisted invitation data.
 * Falls back to inviterName when no invitee first name is provided.
 */
export const getInvitationFirstName = (
  invitation: CoParentInvitation,
): string | undefined => {
  const {rawParams} = invitation;

  const firstName = getFirstName(
    invitation.firstName ||
      rawParams?.first_name ||
      rawParams?.invite_name ||
      rawParams?.inviteName ||
      '',
  );

  return firstName;
};

/**
 * Resolves the invited co-parent last name from persisted invitation data.
 */
export const getInvitationLastName = (
  invitation: CoParentInvitation,
): string | undefined => {
  const {rawParams} = invitation;

  return (
    invitation.lastName ||
    rawParams?.last_name ||
    rawParams?.lastName ||
    undefined
  );
};

/**
 * Normalizes invitation phone values (e.g. `16025544999`) to E.164 (`+16025544999`).
 */
export const normalizeInvitationPhone = (phone: string): string => {
  if (!phone) return '';

  const trimmedPhone = phone.trim();
  const digitsOnly = trimmedPhone.replace(/\D/g, '');
  if (!digitsOnly) return '';

  return `+${digitsOnly}`;
};

/**
 * Formats a list of names into a human-readable string:
 *   ['Riley']                  -> 'Riley'
 *   ['Riley', 'Jerome']        -> 'Riley and Jerome'
 *   ['Riley', 'Jerome', 'Max'] -> 'Riley, Jerome and Max'
 */
export const formatNamesList = (names: string[]): string => {
  if (names.length === 0) return '';
  if (names.length === 1) return names[0];

  const allButLast = names.slice(0, -1).join(', ');
  const last = names[names.length - 1];
  return `${allButLast} and ${last}`;
};
